import { AnalyticsAPI } from '@core/api';
import { SupportedEntitlements } from '@core/models/entitlements';
import { Reports } from '@core/models/reports/reports.models';
import { useEntitlements } from '@hooks/useEntitlements';
import { useImageryFlags } from '@hooks/useImageryFlags';
import { useQueries } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Dictionary } from 'redux-ngrx-entity';
import { useOrganization } from './useOrganization';

export function useReports() {
  const entitlements = useEntitlements();
  const { organization } = useOrganization();
  const { FlagNewReportsDrawer } = useImageryFlags(organization);
  const hasAccessToReportsDrawer =
    FlagNewReportsDrawer || entitlements.has(SupportedEntitlements.WEEKLY_FIELD_INSIGHTS_REPORT);
  const [reportStatuses, setReportStatuses] = useState<
    Dictionary<Reports.ReportStatus | undefined>
  >({});

  const generatedAtDatesRef = useRef<Dictionary<string | undefined>>({});

  const POLLED_REPORT_TYPES = [
    Reports.ReportTypes.NDVI_CHANGE,
    Reports.ReportTypes.VARIABILITY_CHANGE,
  ];

  const queries = useQueries({
    queries: POLLED_REPORT_TYPES.map((type) => ({
      queryKey: [type, { workspace_id: organization?.workspace_id }],
      queryFn: async () => {
        const operation = type === Reports.ReportTypes.NDVI_CHANGE ? 'vegetation' : 'variability';
        const { data } = await AnalyticsAPI.get<Reports.ReportDTO>(
          `/reports/operations/${operation}/workspace/${organization?.workspace_id}`
        );

        setReportStatuses({
          ...reportStatuses,
          [type]: data?.status,
        });

        return { type, ...data, generated_at: generatedAtDatesRef.current[type], is_new: true };
      },
      refetchOnWindowFocus: false,
      enabled:
        Boolean(organization) &&
        hasAccessToReportsDrawer &&
        reportStatuses[type] === Reports.ReportStatus.GENERATING,
      refetchInterval: () =>
        reportStatuses[type] === Reports.ReportStatus.GENERATING ? 1000 * 30 : false,
    })),
  });

  function triggerReportGeneration(type: string, currentDate?: string) {
    if (currentDate) {
      generatedAtDatesRef.current = {
        ...generatedAtDatesRef.current,
        [type]: currentDate,
      };
    }
    const queryIndex = Object.values(Reports.ReportTypes).indexOf(type as Reports.ReportTypes);
    if (queryIndex !== -1) {
      queries[queryIndex].refetch();
    }
  }
  useEffect(() => {
    return () => {
      setReportStatuses({});
      generatedAtDatesRef.current = {};
    };
  }, []);

  return { queries, triggerReportGeneration };
}
